import React, { useCallback, useState, useEffect } from "react";
import { DateTime } from "enums";
import { LocalizationProvider, StaticTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Popover from "@mui/material/Popover";
import MuiTextField from "@mui/material/TextField";
import { AccessTimeOutlined } from "@material-ui/icons";
import { Box } from "@material-ui/core";
import Button from "../button/button";
import DatePicker from "../date-picker/date-picker";
import locale from "localization";
import { combineDateAndTimeInput } from "utils";
import { timePickerPurple } from "../time-picker-v2/styles-object";
import moment from "moment";
import classNames from "classnames";
import styles from "./date-time-range-picker.module.scss";

const TIME_FORMAT = "hh:mm a";

const DateTimeRangePicker = ({ value, name, onChange, className }) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [startTimeText, setStartTimeText] = useState("");
  const [endTimeText, setEndTimeText] = useState("");
  const [startAnchor, setStartAnchor] = useState(null);
  const [endAnchor, setEndAnchor] = useState(null);
  const [startPickerVal, setStartPickerVal] = useState(null);
  const [endPickerVal, setEndPickerVal] = useState(null);

  useEffect(() => {
    if (!value?.startDate && !value?.endDate) {
      setStartDate(null);
      setEndDate(null);
      setStartTime(null);
      setEndTime(null);
      setStartTimeText("");
      setEndTimeText("");
      setStartPickerVal(null);
      setEndPickerVal(null);
    }
  }, [value?.startDate, value?.endDate]);

  const emit = useCallback(
    (sd, st, ed, et) => {
      if (!onChange) return;
      const startDateTime = sd ? (st ? combineDateAndTimeInput(sd, st) : sd) : null;
      const endDateTime = ed ? (et ? combineDateAndTimeInput(ed, et) : ed) : null;
      onChange(name, { value: { startDate: startDateTime, endDate: endDateTime } });
    },
    [name, onChange]
  );

  const handleStartDateChange = useCallback(
    (_, { value: date }) => {
      setStartDate(date);
      emit(date, startTime, endDate, endTime);
    },
    [emit, startTime, endDate, endTime]
  );

  const handleEndDateChange = useCallback(
    (_, { value: date }) => {
      setEndDate(date);
      emit(startDate, startTime, date, endTime);
    },
    [emit, startDate, startTime, endTime]
  );

  const handleStartTimeOk = useCallback(() => {
    const val = startPickerVal || new Date();
    setStartTime(val);
    setStartTimeText(moment(val).format(TIME_FORMAT));
    setStartAnchor(null);
    emit(startDate, val, endDate, endTime);
  }, [startPickerVal, startDate, endDate, endTime, emit]);

  const handleEndTimeOk = useCallback(() => {
    const val = endPickerVal || new Date();
    setEndTime(val);
    setEndTimeText(moment(val).format(TIME_FORMAT));
    setEndAnchor(null);
    emit(startDate, startTime, endDate, val);
  }, [endPickerVal, startDate, startTime, endDate, emit]);

  return (
    <div className={classNames(styles.container, className)}>
      {/* ── Start row ── */}
      <div className={styles.row}>
        <DatePicker
          name="startDate"
          value={startDate}
          placeholder={DateTime.A}
          className={styles.datePicker}
          onChange={handleStartDateChange}
          disableEndDate={endDate}
        />
        <div className={styles.timeWrap}>
          <MuiTextField
            value={startTimeText}
            placeholder="Time"
            className={styles.timeInput}
            InputProps={{
              endAdornment: (
                <AccessTimeOutlined
                  className={styles.clockIcon}
                  onClick={(e) => setStartAnchor(e.currentTarget)}
                />
              ),
              readOnly: true,
            }}
            onClick={(e) => setStartAnchor(e.currentTarget)}
          />
          <Popover
            open={Boolean(startAnchor)}
            anchorEl={startAnchor}
            onClose={() => setStartAnchor(null)}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <StaticTimePicker
                sx={timePickerPurple}
                componentsProps={{ actionBar: { actions: [] } }}
                value={startPickerVal || startTime || new Date()}
                onChange={setStartPickerVal}
                renderInput={(params) => <MuiTextField {...params} />}
              />
              <Box display="flex" justifyContent="flex-end" margin="10px 5px">
                <Button
                  variant="text"
                  className={styles.btnSky}
                  onClick={() => setStartAnchor(null)}
                >
                  <b>{locale.cancel}</b>
                </Button>
                <Button variant="text" className={styles.btnSky} onClick={handleStartTimeOk}>
                  <b>{locale.ok}</b>
                </Button>
              </Box>
            </LocalizationProvider>
          </Popover>
        </div>
      </div>

      <div className={styles.divider}>
        <span className={styles.toLabel}>{locale.to}</span>
      </div>

      {/* ── End row ── */}
      <div className={styles.row}>
        <DatePicker
          name="endDate"
          value={endDate}
          placeholder={DateTime.A}
          className={styles.datePicker}
          onChange={handleEndDateChange}
          disableStartDate={startDate}
        />
        <div className={styles.timeWrap}>
          <MuiTextField
            value={endTimeText}
            placeholder="Time"
            className={styles.timeInput}
            InputProps={{
              endAdornment: (
                <AccessTimeOutlined
                  className={styles.clockIcon}
                  onClick={(e) => setEndAnchor(e.currentTarget)}
                />
              ),
              readOnly: true,
            }}
            onClick={(e) => setEndAnchor(e.currentTarget)}
          />
          <Popover
            open={Boolean(endAnchor)}
            anchorEl={endAnchor}
            onClose={() => setEndAnchor(null)}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <StaticTimePicker
                sx={timePickerPurple}
                componentsProps={{ actionBar: { actions: [] } }}
                value={endPickerVal || endTime || new Date()}
                onChange={setEndPickerVal}
                renderInput={(params) => <MuiTextField {...params} />}
              />
              <Box display="flex" justifyContent="flex-end" margin="10px 5px">
                <Button variant="text" className={styles.btnSky} onClick={() => setEndAnchor(null)}>
                  <b>{locale.cancel}</b>
                </Button>
                <Button variant="text" className={styles.btnSky} onClick={handleEndTimeOk}>
                  <b>{locale.ok}</b>
                </Button>
              </Box>
            </LocalizationProvider>
          </Popover>
        </div>
      </div>
    </div>
  );
};

export default DateTimeRangePicker;
